import React, { useState, useEffect, useCallback } from 'react'
import { Box, Card, CardContent, Skeleton, Stack, Typography, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material'
import { useSnackbar } from 'notistack'
import { postGeneric } from '../../common/constants'
import { formatDate } from '../../helpers/UtilityHelper'

const API_FMT = "yyyy-MM-dd'T'HH:mm:ss"
const C = {
  accent: '#2f6df0', ink: '#1e2530', inkSoft: '#5a6472', inkFaint: '#8b94a3', line: '#e3e7ec',
  good: '#35a054', goodBg: '#e7f4ea', goodInk: '#1e6b34',
  warn: '#e0a52a', warnBg: '#fdf3dd', warnInk: '#8a5d07',
  crit: '#d9534a', critBg: '#fdeae8', critInk: '#a03024',
}
const fmt = (v) => (v == null ? '—' : Math.round(v).toLocaleString('en-IN'))
const bandInk = (v) => (v <= 2000 ? C.goodInk : v <= 5000 ? C.warnInk : C.critInk)
const pct = (cur, prev) => (prev ? ((cur - prev) / prev) * 100 : 0)
const hhmm = (ms) => { const d = new Date(ms); return ('0' + d.getHours()).slice(-2) + ':' + ('0' + d.getMinutes()).slice(-2) }
const fmtDur = (m) => { m = Math.round(m); if (m < 60) return `${m}m`; const h = Math.floor(m / 60), mm = m % 60; return mm ? `${h}h ${mm}m` : `${h}h` }

// Merchant-panel trend card — same visuals as the admin one, but hits the merchant-panel endpoint
// scoped to the SELECTED account (merchantId, validated server-side) and top movers list couriers only.
export default function OrderCreationLatencyTrend({ range, merchantId, courierParents }) {
  const { enqueueSnackbar } = useSnackbar()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)

  const load = useCallback(() => {
    const to = range.to, seriesFrom = range.from // trend spans exactly the selected window (drives everything)
    const spanMin = (to.getTime() - seriesFrom.getTime()) / 60000
    // finer buckets for short windows so the line still has points; coarser for long ones
    const bucketMinutes = spanMin <= 20 ? 1 : spanMin <= 60 ? 2 : spanMin <= 180 ? 5 : spanMin <= 420 ? 15 : 60
    setLoading(true)
    postGeneric('tools/order/creation-latency/merchant/trend', {
      seriesFrom: formatDate(seriesFrom, API_FMT),
      seriesTo: formatDate(to, API_FMT),
      merchantOids: merchantId ? [merchantId] : [],
      bucketMinutes, courierParents,
    })
      .then((res) => setData(res.data?.responseObj || null))
      .catch(() => enqueueSnackbar('Failed to load trend', { variant: 'error' }))
      .finally(() => setLoading(false))
  }, [range, merchantId, courierParents, enqueueSnackbar])

  useEffect(() => { load() }, [load])

  const th = data?.thisHour, lh = data?.lastHour
  const MIN_SAMPLE = 30 // below this a P95 comparison is statistical noise
  const hasThis = !!th && th.shipments > 0
  const hasLast = !!lh && lh.shipments > 0
  const anyData = hasThis || hasLast
  const canCompare = hasThis && hasLast && th.shipments >= MIN_SAMPLE && lh.shipments >= MIN_SAMPLE && lh.p95Ms > 0
  const d95 = canCompare ? pct(th.p95Ms, lh.p95Ms) : 0
  const up = d95 >= 0
  const dVol = canCompare ? pct(th.shipments, lh.shipments) : 0

  // The KPI compares the two HALVES of the selected window. For a ~2h window each half is ~1h, so we
  // keep the familiar "this hour / last hour" wording; otherwise label the halves by their duration.
  const winMin = (range.to.getTime() - range.from.getTime()) / 60000
  const halfDur = fmtDur(winMin / 2)
  const isHourly = winMin / 2 >= 50 && winMin / 2 <= 75
  const recentLbl = isHourly ? 'this hour' : `recent ${halfDur}`
  const earlierLbl = isHourly ? 'last hour' : `earlier ${halfDur}`

  // shown instead of a misleading delta when a comparison isn't valid
  let noCompareNote = null
  if (anyData && !canCompare) {
    if (!hasThis) noCompareNote = `No orders in the ${recentLbl}`
    else if (!hasLast) noCompareNote = `No ${earlierLbl} baseline`
    else noCompareNote = `Sample too small to compare (${recentLbl} ${fmt(th.shipments)} / ${earlierLbl} ${fmt(lh.shipments)})`
  }

  // verdict fusing latency delta with volume delta
  // The pill already shows the % + ms, so the verdict adds only the interpretation (no number repeat).
  let verdict = null, vc = 'good'
  if (canCompare) {
    if (!up) { vc = 'good'; verdict = 'Trending down.' }
    else if (Math.abs(dVol) < 15) { vc = 'bad'; verdict = 'Flat volume — likely a real regression. Check top movers.' }
    else if (dVol >= 15) { vc = 'warn'; verdict = 'Volume is up too — likely load-driven.' }
    else { vc = 'warn'; verdict = 'Volume fell while P95 rose — worth a look.' }
  }
  const vColors = { good: [C.goodBg, C.goodInk], warn: [C.warnBg, C.warnInk], bad: [C.critBg, C.critInk] }

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="baseline" flexWrap="wrap">
          <Typography sx={{ fontWeight: 650, fontSize: 16 }}>Trend</Typography>
          <Typography variant="caption" color="text.secondary">{isHourly ? 'Last 60 min vs previous 60 min' : `Recent ${halfDur} vs earlier ${halfDur}`} · P95 is the primary signal</Typography>
        </Stack>

        {loading ? <TrendSkeleton /> : !anyData ? (
          <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>No latency recorded in the selected window.</Typography>
        ) : (
          <>
            {/* KPI banner */}
            <Stack direction="row" sx={{ mt: 1.5, border: `1px solid ${C.line}`, borderRadius: '10px', flexWrap: 'wrap' }}>
              <Box sx={{ p: '12px 18px' }}>
                <Typography sx={{ fontSize: 11, letterSpacing: '.05em', textTransform: 'uppercase', color: C.inkFaint }}>{isHourly ? 'This hour' : `Recent ${halfDur}`} · P95</Typography>
                {hasThis ? (
                  <>
                    <Typography sx={{ fontSize: 28, fontWeight: 750, lineHeight: 1.1, color: bandInk(th.p95Ms), fontVariantNumeric: 'tabular-nums' }}>
                      {fmt(th.p95Ms)} <span style={{ fontSize: 12, fontWeight: 500, color: C.inkFaint }}>ms</span>
                    </Typography>
                    <Typography sx={{ fontSize: 13, color: C.inkSoft }}>
                      {hasLast ? `was ${fmt(lh.p95Ms)} ms ${earlierLbl}` : `no ${earlierLbl} data`} · {fmt(th.shipments)} orders
                    </Typography>
                  </>
                ) : (
                  <Typography sx={{ fontSize: 20, fontWeight: 700, color: C.inkFaint, mt: 0.5 }}>— <span style={{ fontSize: 12, fontWeight: 500 }}>no orders ({recentLbl})</span></Typography>
                )}
              </Box>
              <Box sx={{ p: '12px 18px', borderLeft: `1px solid ${C.line}`, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 0.75, flex: 1 }}>
                {canCompare ? (
                  <>
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, px: 1.75, py: 0.75, borderRadius: '10px', fontWeight: 750, fontSize: 15, bgcolor: up ? C.critBg : C.goodBg, color: up ? C.critInk : C.goodInk, alignSelf: 'flex-start' }}>
                      <span style={{ fontSize: 19 }}>{up ? '▲' : '▼'}</span>
                      <span>P95 {Math.abs(d95).toFixed(0)}% {up ? 'slower' : 'faster'}
                        <span style={{ fontWeight: 500, fontSize: 12, opacity: .85 }}> &nbsp;({up ? '+' : '−'}{fmt(Math.abs(th.p95Ms - lh.p95Ms))} ms)</span>
                      </span>
                    </Box>
                    {verdict && (
                      <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: vColors[vc][1], display: 'flex', gap: 0.75, alignItems: 'flex-start' }}>
                        <span style={{ fontSize: 14, lineHeight: 1.3 }}>{vc === 'good' ? '✓' : '⚠'}</span><span>{verdict}</span>
                      </Typography>
                    )}
                  </>
                ) : (
                  <Typography variant="body2" sx={{ color: C.inkSoft, fontStyle: 'italic' }}>{noCompareNote}</Typography>
                )}
              </Box>
              {hasThis && (
                <>
                  <MiniStat k="P99" v={fmt(th.p99Ms)} unit="ms" d={canCompare ? pct(th.p99Ms, lh.p99Ms) : null} />
                  <MiniStat k="Avg" v={fmt(th.avgMs)} unit="ms" d={canCompare ? pct(th.avgMs, lh.avgMs) : null} />
                  <MiniStat k="Volume" v={fmt(th.shipments)} unit="orders" d={canCompare ? dVol : null} />
                </>
              )}
            </Stack>

            {/* chart + top movers */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.15fr .85fr' }, gap: 2, mt: 2 }}>
              <Box>
                <SectionTitle main="Latency over time" hint="P95 (line) · avg (faint) · volume (bars)" />
                <TrendChart
                  series={data.series}
                  splitMs={(range.from.getTime() + range.to.getTime()) / 2}
                  leftLabel={isHourly ? 'last hour' : 'earlier half'}
                  rightLabel={isHourly ? 'this hour' : 'recent half'}
                />
                <Stack direction="row" spacing={2} sx={{ mt: 1 }} flexWrap="wrap">
                  <LegendSwatch color={C.accent} text="P95" />
                  <LegendSwatch color="#8b5cf6" text="Avg" />
                  <LegendSwatch color="rgba(30,37,48,.12)" text="Shipments" />
                </Stack>
              </Box>
              <Box>
                <SectionTitle main="Top movers" hint="biggest P95 slowdown · slow couriers (≥2s) only" />
                <MoversTable movers={data.topMovers} isHourly={isHourly} />
              </Box>
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  )
}

// Inline skeleton (not a full-screen loader) so first load has structure, no modal overlay.
const TrendSkeleton = () => (
  <Box sx={{ mt: 1.5 }}>
    <Skeleton variant="rounded" height={90} sx={{ mb: 1.25 }} />
    <Stack direction="row" spacing={1.25} sx={{ mb: 1.5 }}>
      <Skeleton variant="rounded" width={120} height={56} />
      <Skeleton variant="rounded" width={120} height={56} />
      <Skeleton variant="rounded" width={120} height={56} />
    </Stack>
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.15fr .85fr' }, gap: 2 }}>
      <Skeleton variant="rounded" height={260} />
      <Skeleton variant="rounded" height={260} />
    </Box>
  </Box>
)

const MiniStat = ({ k, v, unit, d }) => {
  const hasD = d != null
  const flat = hasD && Math.abs(d) < 8
  const col = !hasD ? C.inkFaint : flat ? C.inkSoft : (d > 0 ? C.critInk : C.goodInk)
  const arrow = !hasD ? '' : flat ? '→' : (d > 0 ? '▲' : '▼')
  return (
    <Box sx={{ p: '12px 18px', borderLeft: `1px solid ${C.line}`, minWidth: 96, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
      <Typography sx={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.04em', color: C.inkFaint }}>{k}</Typography>
      <Typography sx={{ fontSize: 15, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{v} <span style={{ fontSize: 11, color: C.inkFaint }}>{unit}</span></Typography>
      <Typography sx={{ fontSize: 12, fontWeight: 600, color: col }}>{hasD ? `${arrow} ${Math.abs(d).toFixed(0)}% vs earlier` : 'vs earlier n/a'}</Typography>
    </Box>
  )
}

const SectionTitle = ({ main, hint }) => (
  <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 1 }}>{main} <span style={{ fontWeight: 400, fontSize: 11.5, color: C.inkFaint }}>· {hint}</span></Typography>
)
const LegendSwatch = ({ color, text }) => (
  <Stack direction="row" spacing={0.75} alignItems="center">
    <Box sx={{ width: 20, height: 9, borderRadius: '2px', bgcolor: color }} />
    <Typography variant="caption" color="text.secondary">{text}</Typography>
  </Stack>
)

// Single merchant → a mover lane is just a courier (the merchant column would repeat the brand name).
const MoversTable = ({ movers, isHourly }) => {
  if (!movers || !movers.length) return <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>{isHourly
    ? 'No courier lane got slower this hour (P95 ≥ 2s, ≥20 orders in both hours) — nothing degrading right now.'
    : 'No courier lane got slower in the recent half (P95 ≥ 2s, ≥20 orders in both halves) — nothing degrading right now.'}</Typography>
  return (
    <Table size="small" sx={{ '& td, & th': { whiteSpace: 'nowrap', px: 1, borderColor: C.line } }}>
      <TableHead>
        <TableRow>
          <TableCell sx={{ fontSize: 11, color: C.inkFaint }}>Courier</TableCell>
          <TableCell align="right" sx={{ fontSize: 11, color: C.inkFaint }}>P95 last → this (ms)</TableCell>
          <TableCell align="right" sx={{ fontSize: 11, color: C.inkFaint }}>Δ</TableCell>
          <TableCell align="right" sx={{ fontSize: 11, color: C.inkFaint }}>Ship</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {movers.map((m, i) => {
          const u = m.deltaPct >= 0
          return (
            <TableRow key={i}>
              <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>{m.courierParent}</TableCell>
              <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontSize: 13 }}>{fmt(m.lastHourP95Ms)} → {fmt(m.thisHourP95Ms)}</TableCell>
              <TableCell align="right">
                <Box component="span" sx={{ display: 'inline-block', whiteSpace: 'nowrap', px: 1, py: '1px', borderRadius: '999px', fontSize: 12, fontWeight: 700, bgcolor: u ? C.critBg : C.goodBg, color: u ? C.critInk : C.goodInk }}>
                  {u ? '▲' : '▼'} {Math.abs(m.deltaPct).toFixed(0)}%
                </Box>
              </TableCell>
              <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontSize: 13 }}>{fmt(m.shipments)}</TableCell>
            </TableRow>
          )
        })}
      </TableBody>
    </Table>
  )
}

// Chart: static layers as an SVG string, plus an interactive overlay (moving chip + guideline) as JSX.
const TrendChart = ({ series, splitMs, leftLabel, rightLabel }) => {
  const [hi, setHi] = useState(-1) // hovered bucket index; -1 => default to the latest point
  const S = series || []
  if (S.length < 2) return <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>Not enough data points to plot.</Typography>
  const W = 640, H = 300, mL = 42, mR = 14, mT = 12, mB = 28, pW = W - mL - mR, pH = H - mT - mB
  const n = S.length
  const maxP95 = Math.max.apply(null, S.map((p) => p.p95Ms))
  const yMax = Math.max(6000, Math.ceil((maxP95 * 1.12) / 1000) * 1000)
  const maxShip = Math.max(1, Math.max.apply(null, S.map((p) => p.shipments)))
  const X = (i) => mL + (i / (n - 1)) * pW
  const Y = (v) => mT + pH - (Math.min(v, yMax) / yMax) * pH
  const VY = (s) => mT + pH - (s / maxShip) * 46

  let bg = ''
  bg += `<rect x="${mL}" y="${Y(2000)}" width="${pW}" height="${Y(0) - Y(2000)}" fill="rgba(53,160,84,.06)"/>`
  bg += `<rect x="${mL}" y="${Y(5000)}" width="${pW}" height="${Y(2000) - Y(5000)}" fill="rgba(224,165,42,.07)"/>`
  bg += `<rect x="${mL}" y="${Y(yMax)}" width="${pW}" height="${Y(5000) - Y(yMax)}" fill="rgba(217,83,74,.08)"/>`
  for (let g = 0; g <= yMax; g += 2000) {
    bg += `<line x1="${mL}" y1="${Y(g)}" x2="${W - mR}" y2="${Y(g)}" stroke="#eceff3"/>`
    bg += `<text x="${mL - 6}" y="${Y(g) + 4}" text-anchor="end" font-size="10" fill="#8b94a3">${g / 1000}s</text>`
  }
  bg += `<line x1="${mL}" y1="${Y(5000)}" x2="${W - mR}" y2="${Y(5000)}" stroke="#d9534a" stroke-dasharray="4 4" opacity=".5"/>`
  // split at the window MIDPOINT (matches the KPI's recent-half vs earlier-half comparison)
  const bi = S.findIndex((p) => p.bucketStartMs >= splitMs)
  if (bi > 0) {
    const bx = X(bi)
    bg += `<rect x="${mL}" y="${mT}" width="${bx - mL}" height="${pH}" fill="rgba(30,37,48,.035)"/>`
    bg += `<line x1="${bx}" y1="${mT}" x2="${bx}" y2="${mT + pH}" stroke="#aab3c0" stroke-dasharray="3 3"/>`
    bg += `<text x="${(mL + bx) / 2}" y="${mT + 11}" text-anchor="middle" font-size="10" fill="#8b94a3">${leftLabel}</text>`
    bg += `<text x="${(bx + (W - mR)) / 2}" y="${mT + 11}" text-anchor="middle" font-size="10" fill="#5a6472" font-weight="700">${rightLabel}</text>`
  }
  const bw = (pW / n) * 0.5
  S.forEach((p, i) => { bg += `<rect x="${X(i) - bw / 2}" y="${VY(p.shipments)}" width="${bw}" height="${mT + pH - VY(p.shipments)}" fill="rgba(30,37,48,.09)" rx="1"/>` })
  let la = ''; S.forEach((p, i) => { la += (i ? 'L' : 'M') + X(i) + ' ' + Y(p.avgMs) + ' ' })
  bg += `<path d="${la}" fill="none" stroke="#8b5cf6" stroke-width="1.8"/>`
  let lp = '', ar = `M ${X(0)} ${mT + pH} `
  S.forEach((p, i) => { lp += (i ? 'L' : 'M') + X(i) + ' ' + Y(p.p95Ms) + ' '; ar += 'L ' + X(i) + ' ' + Y(p.p95Ms) + ' ' })
  ar += `L ${X(n - 1)} ${mT + pH} Z`
  bg += `<defs><linearGradient id="ptg" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#2f6df0" stop-opacity=".2"/><stop offset="1" stop-color="#2f6df0" stop-opacity="0"/></linearGradient></defs>`
  bg += `<path d="${ar}" fill="url(#ptg)"/><path d="${lp}" fill="none" stroke="#2f6df0" stroke-width="2.5" stroke-linejoin="round"/>`
  const step = Math.max(1, Math.round(n / 5))
  for (let i = 0; i < n; i += step) bg += `<text x="${X(i)}" y="${H - 9}" text-anchor="middle" font-size="10" fill="#8b94a3">${hhmm(S[i].bucketStartMs)}</text>`

  // interactive overlay — a multi-line chip (time · P95 · avg · orders) follows the hovered bucket,
  // defaulting to the latest point. Dots mark both the P95 and avg values at that bucket.
  const a = hi >= 0 ? hi : n - 1
  const P = S[a]
  const cx = X(a), cy = Y(P.p95Ms), ay = Y(P.avgMs)
  const chipW = 140, chipH = 66
  const chipX = Math.max(mL, Math.min(cx - chipW / 2, W - mR - chipW))
  let chipY = cy - chipH - 10
  if (chipY < mT) chipY = cy + 12
  chipY = Math.max(mT, Math.min(chipY, mT + pH - chipH))
  const bwHit = pW / n

  return (
    <Box sx={{ position: 'relative', width: '100%' }}>
      <Box component="svg" viewBox={`0 0 ${W} ${H}`} sx={{ width: '100%', height: 'auto', display: 'block' }} dangerouslySetInnerHTML={{ __html: bg }} />
      <Box component="svg" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet" sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}>
        {S.map((p, i) => (
          <rect key={i} x={X(i) - bwHit / 2} y={mT} width={bwHit} height={pH} fill="transparent"
            style={{ cursor: 'crosshair', pointerEvents: 'all' }}
            onMouseEnter={() => setHi(i)} onMouseLeave={() => setHi(-1)} />
        ))}
        <g style={{ pointerEvents: 'none' }}>
          <line x1={cx} y1={mT} x2={cx} y2={mT + pH} stroke="#2f6df0" strokeOpacity="0.35" strokeDasharray="3 3" />
          <circle cx={cx} cy={ay} r={3.5} fill="#8b5cf6" />
          <circle cx={cx} cy={cy} r={5} fill="#2f6df0" stroke="#fff" strokeWidth="1.5" />
          <rect x={chipX} y={chipY} width={chipW} height={chipH} rx="6" fill="#1e2530" opacity="0.96" />
          <text x={chipX + 10} y={chipY + 15} fontSize="10" fill="#aab3c0">{hhmm(P.bucketStartMs)}</text>
          <circle cx={chipX + 13} cy={chipY + 28} r={3.5} fill="#4d8bf0" />
          <text x={chipX + 22} y={chipY + 31} fontSize="11" fontWeight="700" fill="#fff">{`P95  ${fmt(P.p95Ms)} ms`}</text>
          <circle cx={chipX + 13} cy={chipY + 42} r={3.5} fill="#8b5cf6" />
          <text x={chipX + 22} y={chipY + 45} fontSize="11" fill="#e8ecf3">{`Avg  ${fmt(P.avgMs)} ms`}</text>
          <circle cx={chipX + 13} cy={chipY + 56} r={3.5} fill="#8b94a3" />
          <text x={chipX + 22} y={chipY + 59} fontSize="11" fill="#cbd5e1">{`${fmt(P.shipments)} orders`}</text>
        </g>
      </Box>
    </Box>
  )
}
